import { Stack } from '@mui/material'
import { ReactElement, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { Kap_Goal, Milestone, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import { FinalProgramReportExportMenu } from 'src/screens/kap/shared/FinalProgramReportExportMenu'
import { useMilestoneTitle } from 'src/screens/shared/implementation/details/milestone/MilestoneTitleContext'
import { queryMilestoneById } from 'src/screens/shared/implementation/details/milestoneQueries'
import { usePermissionsForKapMilestones } from 'src/service/security/PermissionHook'
import { GOAL_CHAR } from 'src/shared/constants/constants'
import { MILESTONE_RESPONSIBLE_TYPE_TYPE, MILESTONE_STATUS_TYPE } from 'src/shared/constants/milestone-constants'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { ExpandableGoalMeasureCard } from 'src/shared/presentation/ExpandableGoalMeasureCard'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { Section } from 'src/shared/presentation/Section'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient } from 'urql'
import { KapMilestoneGoalFinalReportCardContent } from './KapMilestoneGoalFinalReportCardContent'
import { queryKapGoalsWithFinalReports } from './kapMilestoneGoalFinalReportQueries'

export const KapMilestoneGoalFinalReportPage = (): ReactElement => {
  const { programId, milestoneId, milestonePath } = useParams()
  const parsedProgramId = parseInt(programId as string)
  const parsedMilestoneId = parseInt(milestoneId as string)

  const { getMessage } = useMessageSource()
  const navigate = useDelayedNavigate()
  const title = useMilestoneTitle()
  const urqlClient = useClient()
  const notificationService = useNotificationService()

  const [milestoneData, setMilestoneData] = useState<Milestone>()
  const [goalsData, setGoalsData] = useState<Kap_Goal[]>([])
  const [dataLoading, setDataLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true)

      const [milestoneResult, goalsResult] = await Promise.all([
        urqlClient
          .query<{ milestone_by_pk: Query_Root['milestone_by_pk'] }, { id: number }>(queryMilestoneById, {
            id: parsedMilestoneId,
          })
          .toPromise(),
        urqlClient
          .query<{ kap_goal: Query_Root['kap_goal'] }, { programId: number; milestoneId: number }>(
            queryKapGoalsWithFinalReports,
            {
              programId: parsedProgramId,
              milestoneId: parsedMilestoneId,
            },
          )
          .toPromise(),
      ])

      if (milestoneResult?.data?.milestone_by_pk) {
        setMilestoneData(milestoneResult.data.milestone_by_pk)
      } else {
        notificationService.operationFailed()
      }

      if (goalsResult.data?.kap_goal) {
        setGoalsData(goalsResult.data.kap_goal)
      } else if (goalsResult.error) {
        notificationService.operationFailed()
      }

      setDataLoading(false)
    }

    fetchData()
  }, [notificationService, parsedMilestoneId, parsedProgramId, urqlClient])

  const {
    loading: permissionsLoading,
    canView,
    canEdit,
  } = usePermissionsForKapMilestones(
    parsedProgramId,
    milestoneData?.status as MILESTONE_STATUS_TYPE,
    milestoneData?.responsible_type as MILESTONE_RESPONSIBLE_TYPE_TYPE,
  )
  const loading = dataLoading || permissionsLoading

  const onBack = () => {
    navigate(ROUTES.KapImplementationDetailsRoot.params({ programId }))
  }

  const onEdit = (goalId: number) =>
    navigate(
      ROUTES.KapFinalReportMilestoneRoot.nested.GoalRatingEdit.params({
        programId,
        milestoneId,
        milestonePath,
        goalId,
      }),
    )
  const onNavigation = (goalId: number) => {
    window.open(ROUTES.KapDetailsRoot.nested.GoalDetails.params({ programId, goalId }))
  }

  if (!loading && !canView) {
    return <NotAuthorized />
  }

  return (
    <ScreenLayout title={title} onBack={onBack} actions={<FinalProgramReportExportMenu />}>
      <PageLayout>
        <Section
          title={getMessage('label.milestone.final.report.goal.rating.kap.title')}
          helpAndInstructions={<HelpAndInstructions labelKey={`label.help.milestone.final.report.goal.rating.kap`} />}
        >
          <Stack spacing={1}>
            {goalsData.map((goal) => (
              <ExpandableGoalMeasureCard
                key={goal.id}
                type={GOAL_CHAR}
                title={goal.name}
                id={`${getMessage('label.goal.id.character')}${goal.sort_number}`}
                entityId={goal.id}
                canEdit={canEdit}
                onEdit={() => onEdit(goal.id)}
                onNavigate={() => onNavigation(goal.id)}
              >
                <KapMilestoneGoalFinalReportCardContent goal={goal} />
              </ExpandableGoalMeasureCard>
            ))}
          </Stack>
        </Section>
      </PageLayout>
    </ScreenLayout>
  )
}
